import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Box, useTheme, Typography, Chip } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { formatCurrency } from '../utils/formatters';

const AlertStatusBadge = ({ alertEnabled }) => {
    return alertEnabled ? 
        <Chip label="ON" size="small" sx={{ bgcolor: '#4caf50', color: 'white', minWidth: 50 }} /> :
        <Chip label="OFF" size="small" sx={{ bgcolor: '#f44336', color: 'white', minWidth: 50 }} />;
};

const HeatMapCell = ({ value, type, maxValue }) => {
    if (!value || value === 0) {
        return <Box sx={{ width: '100%', height: 30, backgroundColor: '#f5f5f5' }} />;
    }

    const intensity = Math.min(value / maxValue, 1);
    
    const colors = {
        critical: {
            light: `rgba(244, 67, 54, ${0.3 + intensity * 0.7})`,
            dark: '#f44336'
        },
        toReview: {
            light: `rgba(255, 152, 0, ${0.3 + intensity * 0.7})`,
            dark: '#ff9800'
        },
        onTrack: {
            light: `rgba(76, 175, 80, ${0.3 + intensity * 0.7})`,
            dark: '#4caf50'
        }
    };

    const color = colors[type] || colors.critical;

    return (
        <Box sx={{ 
            width: '100%', 
            height: 30, 
            backgroundColor: color.light,
            border: `1px solid ${color.dark}`,
            borderRadius: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Typography variant="caption" sx={{ 
                color: intensity > 0.6 ? 'white' : color.dark,
                fontWeight: 'bold',
                fontSize: '0.7rem'
            }}>
                {value}
            </Typography>
        </Box>
    );
};
export
 const ProfitLeaksSummary = ({ data, onRowSelect }) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(new Set());
    const [selectedId, setSelectedId] = useState(null);

    if (!data || data.length === 0) {
        return (
            <Paper sx={{ 
                backgroundColor: 'white',
                border: 'thin solid #323D7225',
                borderRadius: 2,
                boxShadow: '1px 1px #323D7250',
                p: 2 
            }}>
                <Typography variant="h6" color="primary" mb={2}>
                    Profit Leaks Analysis
                </Typography>
                <Typography align="center" color="textSecondary">
                    No data available
                </Typography>
            </Paper>
        );
    }

    const handleExpand = (id) => {
        const next = new Set(expanded);
        next.has(id) ? next.delete(id) : next.add(id);
        setExpanded(next);
    };

    const handleRowSelect = (row) => {
        setSelectedId(row.id);
        if (onRowSelect) {
            onRowSelect(row);
        }
    };

    const renderRow = (row, isSubRow = false) => {
        const hasChildren = row.subRows?.length > 0;
        const isExpanded = expanded.has(row.id);
        const isSelected = selectedId === row.id;

        // Calculate max values for heat map intensity
        const allValues = data.flatMap(r => [r, ...(r.subRows || [])]);
        const maxCritical = Math.max(...allValues.map(r => r.critical || 0));
        const maxToReview = Math.max(...allValues.map(r => r.toReview || 0));
        const maxOnTrack = Math.max(...allValues.map(r => r.onTrack || 0));

        return (
            <TableRow
                key={row.id}
                hover
                selected={isSelected}
                onClick={() => isSubRow ? handleRowSelect(row) : handleExpand(row.id)}
                sx={{
                    cursor: 'pointer',
                    bgcolor: isSubRow ? '#f8f9fa' : 'inherit',
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover
                    }
                }}
            >
                <TableCell sx={{ width: 140 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', pl: isSubRow ? 3 : 0 }}>
                        {hasChildren && !isSubRow && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleExpand(row.id);
                                }}
                                sx={{ mr: 1 }}
                            >
                                {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                            </IconButton>
                        )}
                        {isSubRow ? "" : row.area}
                    </Box>
                </TableCell>

                <TableCell sx={{ width: 200 }}>
                    {row.description}
                </TableCell>

                <TableCell align="center" sx={{ width: 100, p: 1 }}>
                    <HeatMapCell 
                        value={row.critical} 
                        type="critical" 
                        maxValue={maxCritical}
                    />
                </TableCell>

                <TableCell align="center" sx={{ width: 100, p: 1 }}>
                    <HeatMapCell 
                        value={row.toReview} 
                        type="toReview" 
                        maxValue={maxToReview}
                    />
                </TableCell>

                <TableCell align="center" sx={{ width: 100, p: 1 }}>
                    <HeatMapCell 
                        value={row.onTrack} 
                        type="onTrack" 
                        maxValue={maxOnTrack}
                    />
                </TableCell>

                <TableCell align="center" sx={{ width: 120 }}>
                    {row.profitImpact ? formatCurrency(row.profitImpact) : ''}
                </TableCell>

                <TableCell align="center" sx={{ width: 80 }}>
                    {row.percentImpact ? `${row.percentImpact}%` : ''}
                </TableCell>

                <TableCell align="center" sx={{ width: 80 }}>
                    {row.accounts || ''}
                </TableCell>

                <TableCell align="center" sx={{ width: 80 }}>
                    {row.skus || ''}
                </TableCell>

                <TableCell align="center" sx={{ width: 90 }}>
                    {row.salesReps || ''}
                </TableCell>

                <TableCell align="center" sx={{ width: 90 }}>
                    {isSubRow ? (
                        <AlertStatusBadge alertEnabled={row.alertEnabled} />
                    ) : ''}
                </TableCell>
            </TableRow>
        );
    };

    // Calculate totals from actual data
    const totals = data.reduce((acc, row) => {
        acc.profitImpact += row.profitImpact || 0;
        return acc;
    }, { profitImpact: 0 });

    return (
        <Paper sx={{ 
            backgroundColor: 'white',
            border: 'thin solid #323D7225',
            borderRadius: 2,
            boxShadow: '1px 1px #323D7250',
            p: 2 
        }}>
            <Typography variant="h6" color="primary" mb={2}>
                Profit Leaks Analysis
            </Typography>

            <TableContainer>
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell width={140} sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                Area
                            </TableCell>
                            <TableCell width={200} sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                Profit Leak Description
                            </TableCell>
                            <TableCell width={100} align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                Critical
                            </TableCell>
                            <TableCell width={100} align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                To-Review
                            </TableCell>
                            <TableCell width={100} align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                On-Track
                            </TableCell>
                            <TableCell width={120} align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                Profit Impact
                            </TableCell>
                            <TableCell width={80} align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                % of Impact
                            </TableCell>
                            <TableCell width={80} align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                # Accounts
                            </TableCell>
                            <TableCell width={80} align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                # SKUs
                            </TableCell>
                            <TableCell width={90} align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                # Sales Reps
                            </TableCell>
                            <TableCell width={90} align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                Alert?
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map(row => (
                            <React.Fragment key={row.id}>
                                {renderRow(row)}
                                {expanded.has(row.id) &&
                                    row.subRows?.map(sub => renderRow(sub, true))
                                }
                            </React.Fragment>
                        ))}

                        <TableRow sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                            <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>
                                Total
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(totals.profitImpact)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                100%
                            </TableCell>
                            <TableCell colSpan={4} />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};